
import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  Map, 
  Tag, 
  Users, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronLeft,
  ChevronRight,
  ShoppingBag,
  BarChart3,
  LineChart,
  Award,
  MessageSquare,
  Rss,
  Image,
  ImageIcon,
  BookOpen,
  Handshake,
  ClipboardList,
  Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { NotificationBadge } from '@/components/ui/notification-badge';
import { useNotifications } from '@/contexts/NotificationsContext';

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  collapsed: boolean;
  toggleCollapse: () => void;
}

const Sidebar = ({ open, setOpen, collapsed, toggleCollapse }: SidebarProps) => {
  const { isAdmin, signOut } = useAuth();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isMobile = useIsMobile();
  const { unreadCount } = useNotifications();
  
  console.log('Is user admin?', isAdmin());

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMobile && 
        open && 
        sidebarRef.current && 
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, open, setOpen]);

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [location.pathname, isMobile, setOpen]);

  return (
    <>
      {isMobile && open && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40" 
          onClick={() => setOpen(false)}
        />
      )}
      
      <div
        ref={sidebarRef}
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-sidebar transition-all duration-300 border-r border-sidebar-border",
          isMobile 
            ? (open ? "translate-x-0 w-64" : "-translate-x-full") 
            : (open ? (collapsed ? "w-[80px]" : "w-64") : "w-[60px]")
        )}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b border-sidebar-border">
          {open ? (
            <>
              <NavLink to="/dashboard" className="flex items-center space-x-2">
                <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-mono font-bold">
                  T
                </div>
                {!collapsed && (
                  <span className="font-mono font-bold text-lg text-sidebar-foreground">
                    TRAVEL CMS
                  </span>
                )}
              </NavLink>
              
              {!isMobile && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={toggleCollapse}
                  className="hover:bg-sidebar-accent"
                >
                  {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
                  <span className="sr-only">{collapsed ? "Expand" : "Collapse"} sidebar</span>
                </Button>
              )}
              
              {isMobile && (
                <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close sidebar</span>
                </Button>
              )}
            </>
          ) : (
            <div className="w-full flex justify-center">
              <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-mono font-bold">
                T
              </div>
            </div>
          )}
        </div>
        
        <ScrollArea className="h-[calc(100vh-4rem)]">
          <div className={cn("py-4", open ? "px-3" : "px-2")}>
            <div className="space-y-1">
              {open && !collapsed && (
                <p className="text-xs font-semibold text-sidebar-foreground/60 px-4 mb-2 uppercase tracking-wider">
                  Main
                </p>
              )}
              
              <NavItem to="/dashboard" label="Dashboard" icon={<LayoutDashboard className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/blogs" label="Blog Posts" icon={<FileText className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/itineraries" label="Itineraries" icon={<Map className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/premium-itineraries" label="Premium Itineraries" icon={<Award className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem 
                to="/dashboard/itinerary-requests" 
                label="Itinerary Requests" 
                icon={<MessageSquare className="h-5 w-5" />} 
                collapsed={!open || collapsed}
                badgeCount={unreadCount} 
              />
              <NavItem to="/dashboard/short-content" label="Travel Bytes" icon={<BookOpen className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/tags" label="Tags" icon={<Tag className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/products" label="Products" icon={<ShoppingBag className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/affiliate-partners" label="Affiliate Partners" icon={<Handshake className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/deals" label="Deals" icon={<Tag className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/social-cards" label="Social Cards" icon={<ImageIcon className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/images" label="Image Gallery" icon={<Image className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/analytics" label="Analytics" icon={<BarChart3 className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/quiz-analytics" label="Quiz Results" icon={<ClipboardList className="h-5 w-5" />} collapsed={!open || collapsed} />
              <NavItem to="/dashboard/email-signups" label="Email Signups" icon={<Mail className="h-5 w-5" />} collapsed={!open || collapsed} />
              
              {isAdmin() && (
                <>
                  {open && !collapsed && <Separator className="my-4 bg-sidebar-border" />}
                  {open && !collapsed && (
                    <p className="text-xs font-semibold text-sidebar-foreground/60 px-4 mb-2 uppercase tracking-wider">
                      Admin
                    </p>
                  )}
                  <NavItem to="/dashboard/users" label="Users" icon={<Users className="h-5 w-5" />} collapsed={!open || collapsed} />
                  <NavItem to="/dashboard/hero-images" label="Hero Images" icon={<ImageIcon className="h-5 w-5" />} collapsed={!open || collapsed} />
                  <NavItem to="/dashboard/rss-feed-test" label="RSS Feed Tester" icon={<Rss className="h-5 w-5" />} collapsed={!open || collapsed} />
                </>
              )}
              
              {open && !collapsed && <Separator className="my-4 bg-sidebar-border" />}
              {open && !collapsed && (
                <p className="text-xs font-semibold text-sidebar-foreground/60 px-4 mb-2 uppercase tracking-wider">
                  Settings
                </p>
              )}
              <NavItem to="/dashboard/settings" label="Settings" icon={<Settings className="h-5 w-5" />} collapsed={!open || collapsed} />
              
              <Button 
                variant="ghost" 
                className={cn(
                  "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md h-10",
                  open ? (!collapsed ? "w-full justify-start px-3 py-2 text-sm" : "w-full justify-center p-2") : "w-full justify-center p-2"
                )}
                onClick={signOut}
              >
                <LogOut className="h-5 w-5" />
                {open && !collapsed && <span className="ml-2">Logout</span>}
                {(collapsed || !open) && <span className="sr-only">Logout</span>}
              </Button>
            </div>
          </div>
        </ScrollArea>
        
        {!isMobile && !open && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute top-4 right-0 translate-x-1/2 bg-primary text-primary-foreground rounded-full h-6 w-6 flex items-center justify-center shadow-md hover:bg-primary/90"
            onClick={() => setOpen(true)}
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Expand sidebar</span>
          </Button>
        )}
      </div>
      
      {isMobile && !open && (
        <Button 
          variant="ghost" 
          size="icon" 
          className="fixed bottom-4 left-4 z-40 h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-md hover:bg-primary/90" 
          onClick={() => setOpen(true)}
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Open menu</span>
        </Button>
      )}
    </>
  );
};

interface NavItemProps {
  to: string;
  label: string;
  icon: React.ReactNode;
  collapsed?: boolean;
  badgeCount?: number;
}

const NavItem = ({ to, label, icon, collapsed = false, badgeCount = 0 }: NavItemProps) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      cn(
        "flex items-center rounded-md transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground relative",
        collapsed ? "justify-center p-2" : "px-3 py-2 text-sm h-10",
        isActive 
          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium" 
          : "text-sidebar-foreground/80"
      )
    }
    title={collapsed ? label : undefined}
  >
    <span className={collapsed ? "" : "mr-2"}>{icon}</span>
    {!collapsed && label}
    
    {badgeCount > 0 && (
      <NotificationBadge 
        count={badgeCount} 
        className={cn(
          collapsed ? "absolute -top-1 -right-1" : "ml-auto"
        )} 
      />
    )}
  </NavLink>
);

export default Sidebar;
